import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const DEFAULT_HERO = {
  title: "Every seller. One marketplace.",
  subtitle: "Compare offers from independent sellers and buy with confidence — tracked shipping, easy returns, secure checkout.",
  image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
  cta: "Start shopping",
};

function Section({ title, eyebrow, children, testId }) {
  if (!children) return null;
  return (
    <section data-testid={testId} className="max-w-7xl mx-auto px-4 sm:px-8 mt-16">
      <p className="text-xs uppercase tracking-widest text-blue-500 font-bold">{eyebrow}</p>
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1 mb-6">{title}</h2>
      {children}
    </section>
  );
}

function RecentlyViewed() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    try {
      setItems(JSON.parse(localStorage.getItem("vendora_recent") || "[]").slice(0, 4));
    } catch { /* ignore */ }
  }, []);
  if (!items.length) return null;
  return (
    <Section title="Pick up where you left off" eyebrow="Recently viewed" testId="recently-viewed-section">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
      </div>
    </Section>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    api.get("/home").then(({ data }) => setData(data)).catch(() => {});
    api.get("/blog").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => {});
    api.get("/campaigns/active", { params: { placement: "HOME_BANNER" } }).then(({ data }) => setBanners(data)).catch(() => {});
  }, []);

  const trackClick = (id) => api.post("/track", { type: "campaign_click", campaign_id: id }).catch(() => {});

  const hero = data?.hero?.title ? data.hero : DEFAULT_HERO;
  const cats = data?.categories || [];

  return (
    <div data-testid="home-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-8">
        <div className="relative rounded-2xl overflow-hidden bg-slate-900 min-h-[380px] flex items-end">
          <img src={hero.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
          <div className="relative p-8 sm:p-14 max-w-2xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">{hero.title}</h1>
            <p className="text-slate-200 mt-4 text-base sm:text-lg max-w-lg">{hero.subtitle}</p>
            <Link
              to="/search"
              data-testid="hero-cta-btn"
              className="inline-flex items-center gap-2 mt-8 bg-white text-slate-900 font-semibold rounded-full px-7 py-3.5 text-sm hover:bg-blue-500 hover:text-white transition-colors"
            >
              {hero.cta || "Start shopping"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {banners.length > 0 && (
        <section data-testid="campaign-banners" className="max-w-7xl mx-auto px-4 sm:px-8 mt-6 space-y-4">
          {banners.map((b) => (
            <a key={b.id} href={b.link_url || "#"} data-testid={`campaign-banner-${b.id}`} onClick={() => trackClick(b.id)}
              className="relative block rounded-2xl overflow-hidden border border-slate-200 group">
              {b.image_url && <img src={fileUrl(b.image_url)} alt={b.headline || b.name} className="w-full h-36 sm:h-44 object-cover group-hover:scale-[1.02] transition-transform duration-500" />}
              <div className={`${b.image_url ? "absolute inset-0 bg-slate-900/40" : "bg-slate-900"} flex items-center px-8 sm:px-12 py-8`}>
                <div>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-white/80 bg-white/15 px-2.5 py-1 rounded-full">
                    {b.kind === "SPONSOR" ? "Sponsored" : "Advertisement"}
                  </span>
                  <p className="font-heading text-xl sm:text-2xl font-bold text-white mt-2.5">{b.headline || b.name}</p>
                </div>
              </div>
            </a>
          ))}
        </section>
      )}
        <Section title="Shop by category" eyebrow="Browse" testId="categories-section">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 auto-rows-[180px]">
            {cats.slice(0, 5).map((c, i) => (
              <Link
                key={c.id}
                to={`/search?category=${encodeURIComponent(c.name)}`}
                data-testid={`category-tile-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`relative rounded-xl overflow-hidden group ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
              >
                {c.image && <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                <p className="absolute bottom-4 left-4 text-white font-heading font-bold text-lg">{c.name}</p>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {data?.deals?.length > 0 && (
        <Section title="Top deals today" eyebrow="Offers" testId="deals-section">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {data.deals.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </Section>
      )}

      {data?.trending?.length > 0 && (
        <Section title="Trending now" eyebrow="Popular" testId="trending-section">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {data.trending.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </Section>
      )}

      <RecentlyViewed />

      {posts.length > 0 && (
        <Section title="From the blog" eyebrow="Updates" testId="blog-section">
          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.id}`} data-testid={`home-post-${p.id}`} className="group block">
                {p.cover_image && (
                  <div className="rounded-xl overflow-hidden aspect-[16/9] bg-slate-100">
                    <img src={fileUrl(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                )}
                <div className="flex gap-1.5 flex-wrap mt-3">
                  {(p.tags || []).slice(0, 3).map((t) => <span key={t} className="text-[10px] uppercase tracking-widest font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{t}</span>)}
                </div>
                <p className="font-heading font-bold mt-2 group-hover:text-blue-600 transition-colors">{p.title}</p>
                <p className="text-xs text-slate-400 mt-1">{new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
              </Link>
            ))}
          </div>
          <Link to="/blog" data-testid="view-all-posts" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-blue-600 hover:text-blue-700">
            View all posts <ArrowRight className="w-4 h-4" />
          </Link>
        </Section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Truck, t: "Tracked delivery", d: "Every shipment tracked from seller to your door." },
            { icon: RotateCcw, t: "Easy returns", d: "7-day returns with doorstep pickup on eligible items." },
            { icon: ShieldCheck, t: "Seller accountability", d: "Offers ranked by price, speed and seller reliability." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-xl bg-slate-50 p-8">
              <Icon className="w-6 h-6 text-blue-500" />
              <p className="font-heading font-bold mt-4">{t}</p>
              <p className="text-sm text-slate-500 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {data && !data.trending?.length && !cats.length && (
        <div data-testid="empty-marketplace" className="max-w-7xl mx-auto px-8 mt-16 text-center py-20 rounded-2xl bg-slate-50">
          <p className="font-heading text-2xl font-bold">The marketplace is brand new</p>
          <p className="text-slate-500 mt-2">Sellers haven't listed products yet. Be the first to sell.</p>
          <Link to="/sell" data-testid="empty-sell-cta" className="inline-block mt-6 bg-slate-900 text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-slate-700 transition-colors">
            Become a seller
          </Link>
        </div>
      )}
    </div>
  );
}
